// 📁 src/pages/LearningMentorship.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from '../utils/axiosInstance';   // ✅ FIXED: use authenticated axios
import { useAuth } from '../context/AuthContext';

const LearningMentorship = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    folder: '',
    description: '',
    isPaid: false,
    paymentLink: '',
    videoFile: null,
  });
  const [aiSummary, setAiSummary] = useState('');
  const [uploading, setUploading] = useState(false);
  const courseRefs = useRef({});

  /* -----------------------------------------------------
     🔄 Load Courses on Mount
  ----------------------------------------------------- */
  useEffect(() => {
    fetchCourses();
  }, []);

  /* -----------------------------------------------------
     🔍 Auto-scroll to #hash course (optional UX)
  ----------------------------------------------------- */
  useEffect(() => {
    if (window.location.hash && courses.length > 0) {
      const anchor = window.location.hash.substring(1);
      const targetRef = courseRefs.current[anchor];

      if (targetRef?.scrollIntoView) {
        setTimeout(() => {
          targetRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 500);
      }
    }
  }, [courses]);

  /* -----------------------------------------------------
     📥 Fetch all courses (JWT protected)
  ----------------------------------------------------- */
  const fetchCourses = async () => {
    try {
      const res = await axios.get('/api/learning/courses');  // ✅ FIXED URL
      setCourses(res.data);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
    }
  };

  /* -----------------------------------------------------
     📝 Form Handlers
  ----------------------------------------------------- */
  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFile = (e) => {
    setFormData({ ...formData, videoFile: e.target.files[0] });
  };

  /* -----------------------------------------------------
     🤖 AI Summary Generator
  ----------------------------------------------------- */
  const handleAISummary = async () => {
    try {
      const res = await axios.post('/api/learning/ai-summary', {
        description: formData.description,
      });
      setAiSummary(res.data.summary);
    } catch (err) {
      alert('❌ AI summary failed');
    }
  };

  /* -----------------------------------------------------
     ☁️ Upload Video to Cloudinary
  ----------------------------------------------------- */
  const uploadToCloudinary = async (file) => {
    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', 'your_preset');

    const res = await axios.post(
      'https://api.cloudinary.com/v1_1/your_cloud_name/video/upload',
      data
    );
    return res.data.secure_url;
  };

  /* -----------------------------------------------------
     🚀 Create Course (Admin Only)
  ----------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let videoUrl = '';
      if (formData.videoFile) {
        videoUrl = await uploadToCloudinary(formData.videoFile);
      }

      await axios.post('/api/learning/create-course', {
        title: formData.title,
        folder: formData.folder,
        description: formData.description,
        isPaid: formData.isPaid,
        paymentLink: formData.paymentLink,
        videoUrls: videoUrl ? [videoUrl] : [],
      });

      // Reset form
      setFormData({
        title: '',
        folder: '',
        description: '',
        isPaid: false,
        paymentLink: '',
        videoFile: null,
      });

      setAiSummary('');
      fetchCourses();
      alert('✅ Course uploaded!');
    } catch (err) {
      alert('❌ Upload failed');
    } finally {
      setUploading(false);
    }
  };

  /* -----------------------------------------------------
     🎨 UI
  ----------------------------------------------------- */
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">📚 Learning & Mentorship Lab</h2>

      {/* 🔐 Admin-only Course Creator */}
      {isAdmin && (
        <form onSubmit={handleSubmit} className="space-y-4 border p-4 rounded-xl shadow-md bg-white">
          <input
            name="title"
            value={formData.title}
            onChange={handleInput}
            placeholder="Course Title"
            className="w-full p-2 border rounded"
            required
          />

          <input
            name="folder"
            value={formData.folder}
            onChange={handleInput}
            placeholder="Folder/Category"
            className="w-full p-2 border rounded"
          />

          <textarea
            name="description"
            value={formData.description}
            onChange={handleInput}
            placeholder="Course Description"
            className="w-full p-2 border rounded"
          />

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="isPaid"
                checked={formData.isPaid}
                onChange={handleInput}
              />
              Paid Course?
            </label>

            {formData.isPaid && (
              <input
                name="paymentLink"
                value={formData.paymentLink}
                onChange={handleInput}
                placeholder="Payment Link"
                className="p-2 border rounded"
              />
            )}
          </div>

          <input type="file" accept="video/*" onChange={handleFile} />

          <button
            type="button"
            className="bg-indigo-500 text-white px-4 py-2 rounded"
            onClick={handleAISummary}
          >
            ✨ Get AI Summary
          </button>

          {aiSummary && (
            <div className="bg-gray-100 p-2 rounded text-sm text-gray-700">
              {aiSummary}
            </div>
          )}

          <button
            type="submit"
            className="bg-green-600 text-white px-6 py-2 rounded"
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Create Course'}
          </button>
        </form>
      )}

      {/* 🎬 Course Viewer */}
      <h3 className="mt-10 text-xl font-semibold">🎬 Available Courses</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
        {courses.map((course) => {
          const id = course.folder?.toLowerCase().replace(/\s+/g, '-');
          return (
            <div
              key={course._id}
              ref={(el) => (courseRefs.current[id] = el)}
              className="border p-4 rounded bg-white shadow hover:shadow-lg transition"
            >
              <div className="relative w-full h-48 overflow-hidden rounded mb-3">
                {course.videoUrls?.[0] ? (
                  <video
                    src={course.videoUrls[0]}
                    muted
                    preload="metadata"
                    className="w-full h-full object-cover rounded"
                    onMouseOver={(e) => e.target.play()}
                    onMouseOut={(e) => {
                      e.target.pause();
                      e.target.currentTime = 0;
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
                    No Video
                  </div>
                )}
              </div>

              <h4 className="text-lg font-bold mb-1">{course.title}</h4>
              <p className="text-sm text-gray-600 mb-1">{course.folder}</p>
              <p className="text-sm text-gray-700 mb-2">{course.description}</p>

              {course.isPaid && course.paymentLink && (
                <a
                  href={course.paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline text-sm"
                >
                  🔗 Pay to Access
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LearningMentorship;
